import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Bert } from 'meteor/themeteorchef:bert';

import {
  featureGetZoom,
  featurePointGetLongitude,
  featurePointGetLatitude
} from '../../../modules/geojson-utilities';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import MapContainerProjectEditor from '../../containers/MapContainerProjectEditor';
import Validation from '../../containers/Validation';

class ProjectEditor extends React.Component {
  state = {
    location: get(this.props, 'project.mapLocation')
  };

  changeProjectLocation = (location, bbox) => {
    this.setState({
      location: {
        type: 'Feature',
        bbox,
        geometry: {
          type: 'Point',
          coordinates: [featurePointGetLongitude(location), featurePointGetLatitude(location), 0]
        },
        properties: {
          zoom: featureGetZoom(location)
        }
      }
    });
  };

  changeLatitude = newLat => {
    this.setState(prevState => {
      const prevCoords = prevState.location.geometry.coordinates;
      const prevZoom = prevState.location.properties.zoom;
      const newLoc = {
        location: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [prevCoords[0], newLat, 0]
          },
          properties: {
            zoom: prevZoom
          }
        }
      };
      return newLoc;
    });
  };

  changeLongitude = newLon => {
    this.setState(prevState => {
      const prevCoords = prevState.location.geometry.coordinates;
      const prevZoom = prevState.location.properties.zoom;
      const newLoc = {
        location: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [newLon, prevCoords[1], 0]
          },
          properties: {
            zoom: prevZoom
          }
        }
      };
      return newLoc;
    });
  };

  changeZoom = event => {
    const newZoom = Number(event.target.value);
    this.setState(prevState => {
      const prevCoords = prevState.location.geometry.coordinates;
      const newLoc = {
        location: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [prevCoords[0], prevCoords[1], 0]
          },
          properties: {
            zoom: newZoom
          }
        }
      };
      return newLoc;
    });
  };

  handleSubmit = async form => {
    const existingProject = get(this.props, 'project._id');
    const { location } = this.state;
    const methodToCall = existingProject ? 'projects.update' : 'projects.insert';
    const project = {
      name: form.name.value.trim(),
      description: form.description.value.trim(),
      mapLocation: location
    };

    if (existingProject) project._id = existingProject;

    try {
      const projectId = await methodPromise(methodToCall, project);
      this.redirectToProjectView(form, existingProject, projectId);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  redirectToProjectView(form, existingProject, projectId) {
    const { history } = this.props;
    const confirmation = existingProject ? 'Project updated!' : 'Project added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/projects/${projectId}`);
  }

  render() {
    const { project, deployEnv } = this.props;
    const { location } = this.state;
    const validationProps = {
      rules: {
        name: {
          required: true
        },
        description: {
          required: false
        },
        location: {
          longitude: {
            required: true
          },
          latitude: {
            required: true
          },
          zoom: {
            required: true
          }
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        },
        description: {
          required: 'This needs a description, please.'
        },
        location: {
          longitude: {
            required: 'This needs a longitude, please.'
          },
          latitude: {
            required: 'This needs a latitude, please.'
          },
          zoom: {
            required: 'This needs a zoom, please.'
          }
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };
    return (
      <Row noGutters>
        <Col xs={12} md={4} className="pr-1">
          <Validation {...validationProps}>
            <Form onSubmit={event => event.preventDefault()}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  defaultValue={project && project.name}
                  placeholder="Oh, The Places You'll Go!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  defaultValue={project && project.description}
                  placeholder="Congratulations! Today is your day. You're off to Great Places! You're off and away!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Longitude</Form.Label>
                <Form.Control
                  type="number"
                  name="longitude"
                  max="180"
                  min="-180"
                  readOnly
                  value={featurePointGetLongitude(location)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Latitude</Form.Label>
                <Form.Control
                  type="number"
                  name="latitude"
                  max="90"
                  min="-90"
                  readOnly
                  value={featurePointGetLatitude(location)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Zoom</Form.Label>
                <Form.Control
                  type="number"
                  name="zoom"
                  max="18"
                  min="0"
                  value={featureGetZoom(location)}
                  onChange={this.changeZoom}
                />
              </Form.Group>
              <Row noGutters>
                <Col xs={12}>
                  <Button className="float-right" type="submit" variant="success">
                    {project && project._id ? 'Save Changes' : 'Add Project'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Validation>
        </Col>
        <Col xs={12} md={8}>
          <MapContainerProjectEditor
            location={location}
            onLocationChange={this.changeProjectLocation}
            deployEnv={deployEnv}
            searchItem
            isProjectMap
          />
        </Col>
      </Row>
    );
  }
}

ProjectEditor.defaultProps = {
  project: {
    name: '',
    description: '',
    mapLocation: {
      bbox: [-3.9183425903320317, 40.32560799973207, -3.489189147949219, 40.508056667997295],
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [-3.7041009949613226, 40.416371399358034]
      },
      properties: {
        zoom: 12
      }
    }
  }
};

ProjectEditor.propTypes = {
  project: PropTypes.object,
  history: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired
};

export default ProjectEditor;
